import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { FrequenciaComissaoResponse, FrequenciaMembroResponse, FrequenciaReuniaoResponse } from '@/core/api/sicom-api/comissoes/frequencia/frequencia-comissao.model';

/** Formata a data/hora de início de uma reunião no padrão `dd/mm/aa`, usado tanto na matriz quanto no CSV. */
export function formatarDataReuniao(data?: string | null): string {
    if (!data) {
        return '-';
    }
    const valor = new Date(data);
    if (Number.isNaN(valor.getTime())) {
        return String(data);
    }
    const dia = String(valor.getDate()).padStart(2, '0');
    const mes = String(valor.getMonth() + 1).padStart(2, '0');
    const ano = String(valor.getFullYear()).slice(-2);
    return `${dia}/${mes}/${ano}`;
}

interface LegendaPresenca {
    sigla: string;
    classe: string;
    descricao: string;
}

const DESCRICAO_PRESENCA: Record<string, string> = {
    P: 'Presente',
    V: 'Presente virtual',
    A: 'Ausente',
    J: 'Ausência justificada',
    N: 'Não era membro'
};

/** Matriz de presença (membros × reuniões) da frequência de uma comissão, com legenda e formatação. */
@Component({
    selector: 'app-frequencia-matriz',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './frequencia-matriz.html',
    styleUrls: ['./frequencia-matriz.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FrequenciaMatrizComponent {
    readonly frequencia = input<FrequenciaComissaoResponse | null>(null);

    readonly reunioes = computed<FrequenciaReuniaoResponse[]>(() => this.frequencia()?.reunioes ?? []);
    readonly membros = computed<FrequenciaMembroResponse[]>(() => this.frequencia()?.membros ?? []);

    readonly legenda: LegendaPresenca[] = [
        { sigla: 'P', classe: 'presenca-p', descricao: 'Presente' },
        { sigla: 'V', classe: 'presenca-v', descricao: 'Presente virtual' },
        { sigla: 'A', classe: 'presenca-a', descricao: 'Ausente' },
        { sigla: 'J', classe: 'presenca-j', descricao: 'Ausência justificada' },
        { sigla: 'N-M', classe: 'presenca-n', descricao: 'Não era membro' }
    ];

    formatarData(data?: string | null): string {
        return formatarDataReuniao(data);
    }

    obterPresenca(membro: FrequenciaMembroResponse, idReuniao: number): string | null {
        return membro.presencas?.find((presenca) => presenca.idReuniao === idReuniao)?.presenca ?? null;
    }

    classePresenca(presenca: string | null): string {
        if (!presenca) {
            return 'presenca-vazio';
        }
        switch (presenca.trim().toUpperCase()) {
            case 'P':
                return 'presenca-p';
            case 'V':
                return 'presenca-v';
            case 'A':
                return 'presenca-a';
            case 'J':
                return 'presenca-j';
            case 'N':
                return 'presenca-n';
            default:
                return 'presenca-vazio';
        }
    }

    rotuloPresenca(presenca: string | null): string {
        if (!presenca) {
            return '-';
        }
        const chave = presenca.trim().toUpperCase();
        return chave === 'N' ? 'N-M' : chave;
    }

    tituloPresenca(presenca: string | null): string {
        if (!presenca) {
            return 'Sem registro';
        }
        return DESCRICAO_PRESENCA[presenca.trim().toUpperCase()] ?? presenca;
    }

    formatarPercentual(valor?: number | null): string {
        if (valor === null || valor === undefined) {
            return '—';
        }
        return `${Math.round(valor)}%`;
    }

    /**
     * Quem consta como "não era membro" (N) em todas as reuniões não tem denominador: exibir 0%
     * dava a entender que faltou a tudo, quando na verdade não participava da comissão.
     */
    percentualMembro(membro: FrequenciaMembroResponse): string {
        const reunioesElegiveis = this.reunioes().filter((reuniao) => this.obterPresenca(membro, reuniao.idReuniao) !== 'N');
        if (!reunioesElegiveis.length) {
            return '—';
        }
        return this.formatarPercentual(membro.percentualPresenca);
    }
}
